import React from 'react';
import { node, number, string, array } from 'prop-types';

const Hero = (props) => {
  const {
    heroContent,
    artists = []
  } = props;

  // Extract CMS content with fallback to default values
  let {
    eyebrow,
    title = 'Jewish Art & Judaica',
    subtitle = 'Jewish art, Judaica, and gifts — handmade by independent Jewish artists around the globe.',
  } = heroContent || {};

  // If Sanity has "Handmade" baked into the title instead of the eyebrow field,
  // pull it out so the copper span always gets the word.
  if (!eyebrow && title.toLowerCase().startsWith('handmade ')) {
    eyebrow = 'Handmade';
    title = title.slice('Handmade '.length);
  }
  if (!eyebrow) eyebrow = 'Handmade';

  const [titleLeft, titleRight] = title.includes('&')
    ? title.split('&').map(s => s.trim())
    : [title, null];

  // Process artists for avatars
  // Filter for unique artists with valid profile images
  const uniqueArtists = Array.from(
    new Map(artists.map(artist => [artist.id?.uuid || artist.id, artist])).values()
  );
  
  const artistsWithImages = uniqueArtists
    .filter(artist => artist.profileImage?.attributes?.variants?.['square-small']?.url)
    .sort((a, b) => {
      const ratingA = a.meta?.rating || 0;
      const ratingB = b.meta?.rating || 0;
      return ratingB - ratingA;
    })
    .slice(0, 10);

  return (
    <section
      id="hero"
      className="pt-4 lg:pt-16 px-4 sm:px-6 lg:px-8 relative"
    >
      <div
        className="max-w-7xl mx-auto relative"
      >

        {/* Content */}
        <div className="relative">
          {/* Bento Grid Layout */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 lg:gap-8">

            {/* Main Hero Text Box - Spans 2 columns */}
            <div className="md:col-span-2 bg-white/50 rounded-3xl p-8 md:p-12 border border-gray-100 shadow-sm flex flex-col justify-center items-start space-y-6 h-full">
              <h1 className="text-4xl md:text-4xl lg:text-5xl xl:text-6xl leading-[1.05] tracking-tighter font-['Be_Vietnam_Pro'] m-0">
                <span className="block text-xl md:text-3xl lg:text-4xl font-light text-[#d28a60] tracking-tight">
                  {eyebrow}
                </span>
                {titleRight ? (
                  <>
                    <span className="font-semibold text-black">{titleLeft} </span>
                    <span className="font-light text-black/68">&</span>
                    <span className="font-semibold text-black"> {titleRight}</span>
                  </>
                ) : (
                  <span className="font-semibold text-black">{titleLeft}</span>
                )}
              </h1>

              <p className="text-base sm:text-lg mb-4 md:text-xl lg:text-2xl leading-relaxed text-black font-medium font-['Be_Vietnam_Pro']">
                {subtitle}
              </p>
            </div>

            {/* Right Column Stack - Spans 1 column */}
            <div className="md:col-span-1 flex flex-col gap-4 md:gap-6">
              
              {/* Artists Stats Box */}
              <div className="flex-1 bg-[#F7F7F5] rounded-3xl p-8 border border-gray-200/50 flex flex-col items-center justify-center text-center shadow-sm">
                {artistsWithImages.length > 0 && (
                  <>
                    <div className="flex -space-x-4 justify-center w-full">
                      {artistsWithImages.slice(0, 5).map((artist) => (
                        <div 
                          key={artist.id?.uuid || artist.id} 
                          className="relative z-0 hover:z-10 hover:-translate-y-1 transition-all duration-300"
                        >
                          <img
                            className="w-12 h-12 md:w-14 md:h-14 rounded-full border-[3px] border-white object-cover shadow-md"
                            src={artist.profileImage.attributes.variants['square-small'].url}
                            alt={artist.name || 'Artist'}
                          />
                        </div>
                      ))}
                    </div>
                    <div>
                      <div className="text-3xl md:text-4xl font-bold text-gray-900 font-['Be_Vietnam_Pro']">40+</div>
                      <div className="text-sm md:text-base text-gray-500 font-medium mt-1">Talented Artists</div>
                    </div>
                  </>
                )}
              </div>

              {/* Secondary Action/Info Box (Placeholder for future content or buttons) */}
              <div className="hidden md:flex h-24 bg-[#d28a60]/10 rounded-3xl items-center justify-center border border-[#d28a60]/20 shadow-sm">
                 <span className="text-[#d28a60] font-semibold">Discover Unique Pieces</span>
              </div>

            </div>

          </div>
        </div>
      </div>
    </section>
  );
};

Hero.propTypes = {
  heroContent: node,
  artists: array,
};

export default Hero;
